import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { DashboardService } from '@app/features/dashboards/services/dashboard.service'
import { ScopeUiFilterModalComponent } from '@shared/components/ui-components/scope-ui-filter-modal/scope-ui-filter-modal.component'
import { DateFilterValue, FilterOption, FilterOptions, GenericFilterValue } from '@core/model/filter-option.interface'
import { instanceToInstance, plainToInstance } from 'class-transformer'
import { scopeStatuses } from '@core/model/scope-version'
import { ScopeFilterDefaults } from '@core/model/scope-filter.model'
import { MatDialog } from '@angular/material/dialog'
import { PageEvent } from '@angular/material/paginator'

export class DashboardDataFilter {
  constructor(singleValueFilters: any, multiValueFilters: any, limit = 10) {
    this.singleValueFilters = singleValueFilters
    this.multiValueFilters = multiValueFilters
    this.updatedTs = Date.now()
    this.page = 0
    this.limit = limit
  }

  singleValueFilters: any
  multiValueFilters: any
  updatedTs: any
  page: number
  limit: number
}

@Component({
  selector: 'finance-component',
  templateUrl: './finance.component.html',
  styleUrls: ['./finance.component.scss'],
})
export class FinanceComponent implements OnInit {
  financeReport: any = { scopes: [], pageNumber: 0 }
  currencySymbol = 'USD'
  currentFilters: DashboardDataFilter
  filters!: any
  scopesFilterConfig: Record<string, string>
  filterData: any
  page = 0
  totalCount: number
  pageSizes = [10, 20, 50, 'All']
  filterOptions =
    'SECOND_PARTY, USER, SCOPE_STATUSES, BRANDS, DISCIPLINES, LOCATIONS, SCOPE_TYPES, COMPANY_ACCOUNTS, RATECARDS'
  loading: boolean

  constructor(
    private dashboardService: DashboardService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    this.filters = ScopeFilterDefaults

    this.scopesFilterConfig = {
      companyAccounts: 'Account',
      secondParties: 'Client',
      date: 'Date',
      creators: 'Creator',
      scopeStatuses: 'Status',
      scopeTypes: 'Scope Type',
      ratecards: 'Rate card',
      disciplines: 'Discipline',
      trafficked: 'Trafficked',
    }
  }

  ngOnInit() {
    this.currentFilters = new DashboardDataFilter({}, {})
    this.dashboardService.getFiltersData(this.filterOptions).subscribe({
      next: (rsp) => {
        this.filterData = rsp
        this.cdr.detectChanges()
      },
      error: (err) => {
        this.loading = false
        this.cdr.detectChanges()
        console.error('Error loading filters data:', err)
      },
    })
    this.getFinanceReport()
  }

  getFinanceReport(filters = this.currentFilters) {
    this.loading = true
    this.cdr.detectChanges()
    this.dashboardService.getFinanceReportData(filters).subscribe({
      next: (rsp) => {
        this.loading = false
        this.financeReport = rsp
        this.totalCount = this.financeReport.totalCount
        this.cdr.detectChanges()
      },
      error: (err) => {
        this.loading = false
        console.error('Error loading finance report:', err)
        this.cdr.detectChanges()
      },
    })
  }

  resetFilters() {
    this.filters = ScopeFilterDefaults
    this.currentFilters = new DashboardDataFilter({}, {})
    this.getFinanceReport()
  }

  mapToArray(object: any, valueAsKey = false) {
    return Object.entries(object).reduce((acc: any[], [key, value]) => {
      acc.push(plainToInstance(GenericFilterValue, { id: valueAsKey ? value : key, name: value }))
      return acc
    }, [])
  }

  getDateByTimeframe(value: any) {
    if (!value || !value?.timeframe) {
      return []
    }
    const timeframe = value?.timeframe?.value
    let startDate = new Date()
    let endDate = new Date()

    switch (timeframe) {
      case 'today':
        startDate.setHours(0, 0, 0, 0)
        break
      case 'yesterday':
        startDate.setDate(startDate.getDate() - 1)
        break
      case 'week':
        startDate.setDate(startDate.getDate() - 7)
        break
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1)
        break
      case 'custom':
        startDate = value.start
        endDate = value.end
        break
    }
    return [startDate.toISOString(), endDate.toISOString()]
  }

  private mapIds(values: any[]): any[] {
    return Array.isArray(values) ? values.map((v) => v.id) : []
  }

  onChangePage(pageEvent: PageEvent) {
    this.currentFilters.page = pageEvent.pageIndex
    this.setLimit(pageEvent.pageSize)
    this.loading = true
    this.page = pageEvent.pageIndex
    this.getFinanceReport()
  }

  setLimit(value: number | string) {
    if (value === 'All') {
      this.currentFilters.limit = 10000
    } else if (typeof value == 'number') {
      this.currentFilters.limit = value
    }
  }

  onSelectFilters() {
    const filterModal = this.dialog.open(ScopeUiFilterModalComponent, {
      width: '328px',
      position: { right: '40px' },
      data: {
        options: {
          companyAccounts: new FilterOption<GenericFilterValue[]>(
            this.scopesFilterConfig['companyAccounts'],
            'genericSearch',
            'Select accounts',
            this.filterData?.find((item) => item.key === 'COMPANY_ACCOUNTS')?.values,
            undefined,
            () => this.filterData?.find((item) => item.key === 'COMPANY_ACCOUNTS')?.values.length > 1,
            instanceToInstance(this.filters.companyAccounts)
          ),
          secondParties: new FilterOption<GenericFilterValue[]>(
            this.scopesFilterConfig['secondParties'],
            'genericSearch',
            'Select clients',
            this.filterData?.find((item) => item.key === 'SECOND_PARTY')?.values,
            undefined,
            () => true,
            instanceToInstance(this.filters.secondParties)
          ),
          date: new FilterOption<DateFilterValue>(
            this.scopesFilterConfig['date'],
            'date',
            undefined,
            [{ name: 'Created', value: 'CREATED' }],
            undefined,
            undefined,
            instanceToInstance(this.filters.date)
          ),
          creators: new FilterOption<GenericFilterValue[]>(
            this.scopesFilterConfig['creators'],
            'genericSearch',
            'Select creators',
            this.filterData?.find((item) => item.key === 'USER')?.values,
            undefined,
            () => true,
            instanceToInstance(this.filters.creators)
          ),
          scopeStatuses: new FilterOption<any[]>(
            this.scopesFilterConfig['scopeStatuses'],
            'genericSearch',
            'Select statuses',
            this.mapToArray(scopeStatuses),
            undefined,
            () => true,
            instanceToInstance(this.filters.scopeStatuses)
          ),
          scopeTypes: new FilterOption<any[]>(
            this.scopesFilterConfig['scopeTypes'],
            'genericSearch',
            'Select types',
            this.filterData?.find((item) => item.key === 'SCOPE_TYPES')?.values,
            undefined,
            () => true,
            instanceToInstance(this.filters.scopeTypes)
          ),
          ratecards: new FilterOption<GenericFilterValue[]>(
            this.scopesFilterConfig['ratecards'],
            'genericSearch',
            'Select ratecards',
            this.filterData?.find((item) => item.key === 'RATECARDS')?.values,
            undefined,
            () => true,
            instanceToInstance(this.filters.ratecards)
          ),
          disciplines: new FilterOption<GenericFilterValue[]>(
            this.scopesFilterConfig['disciplines'],
            'genericSearch',
            'Select disciplines',
            this.filterData?.find((item) => item.key === 'DISCIPLINES')?.values,
            undefined,
            () => true,
            instanceToInstance(this.filters.disciplines)
          ),
          trafficked: new FilterOption<boolean>(
            this.scopesFilterConfig['trafficked'],
            'radio',
            undefined,
            [
              { name: 'All', value: null },
              { name: 'Yes', value: true },
              { name: 'No', value: false },
            ],
            undefined,
            () => true,
            this.filters.trafficked
          ),
        },
        filterDefaults: ScopeFilterDefaults,
        reset: () => {
          this.resetFilters()
        },
        seeResults: (filters: FilterOptions) => {
          filterModal.close()
          this.filters = {
            ...this.filters,
            secondParties: filters['secondParties'].value,
            companyAccounts: filters['companyAccounts'].value,
            creators: filters['creators'].value,
            scopeStatuses: filters['scopeStatuses'].value,
            scopeTypes: filters['scopeTypes'].value,
            ratecards: filters['ratecards'].value,
            disciplines: filters['disciplines'].value,
            date: filters['date'].value,
            trafficked: filters['trafficked'].value,
          }
          this.currentFilters = new DashboardDataFilter(
            { ARCHIVED: false, TRAFFICKED: filters['trafficked'].value },
            {
              SECOND_PARTY: this.mapIds(filters['secondParties'].value),
              COMPANY_ACCOUNTS: this.mapIds(filters['companyAccounts'].value),
              USER: this.mapIds(filters['creators'].value),
              SCOPE_STATUSES: this.mapIds(filters['scopeStatuses'].value),
              SCOPE_TYPES: this.mapIds(filters['scopeTypes'].value),
              RATECARDS: this.mapIds(filters['ratecards'].value),
              DISCIPLINES: this.mapIds(filters['disciplines'].value),
              DATE_RANGE: this.getDateByTimeframe(filters['date'].value),
            },
            this.currentFilters.limit
          )
          this.getFinanceReport(this.currentFilters)
        },
      },
    })
  }
}
